import { useState, useCallback } from 'react';
import {
  PlainLink,
  Scrollable,
  SkeletonShape,
  FormattedRelativeTime,
  Flex,
  LabelIcon,
  Text,
  Tooltip,
} from '@customerio/pluma-components/react';
import type { IconName } from '@customerio/pluma-icons';
import type { ServicesApiRecentResource } from '@/services/api/services-api';
import { useAnalytics } from '@/services/analytics';
import { useResourceInfo } from '../hooks';
import { generatePath } from '@/services/router/generate-path';

import * as styles from './recents-row.css';

export function resourceHref(workspaceId: number, resource: ServicesApiRecentResource): string {
  const wid = String(workspaceId);
  switch (resource.type) {
    case 'campaign':
      return generatePath('/workspaces/:workspaceId/journeys/campaigns/:campaignId', {
        workspaceId: wid,
        campaignId: resource.id,
      });
    case 'newsletter':
      return generatePath('/workspaces/:workspaceId/journeys/broadcasts/newsletter/:newsletterId', {
        workspaceId: wid,
        newsletterId: resource.id,
      });
    case 'transactional':
      return generatePath('/workspaces/:workspaceId/journeys/transactional/:transactionalMessageId', {
        workspaceId: wid,
        transactionalMessageId: resource.id,
      });
    case 'segment':
      return generatePath('/workspaces/:workspaceId/journeys/segments/:segmentId', {
        workspaceId: wid,
        segmentId: resource.id,
      });
    case 'in_app_broadcast':
      return generatePath('/workspaces/:workspaceId/journeys/anonymous/:inAppBroadcastId', {
        workspaceId: wid,
        inAppBroadcastId: resource.id,
      });
    case 'ds_node':
      return generatePath('/workspaces/:workspaceId/design-studio/editor/:id', {
        workspaceId: wid,
        id: resource.id,
      });
    case 'snippet':
      return generatePath('/workspaces/:workspaceId/journeys/snippets/:snippetId', {
        workspaceId: wid,
        snippetId: resource.id,
      });
    default: {
      const _exhaustive: never = resource.type;
      return String(_exhaustive);
    }
  }
}

type RecentResourceWithDisplayName = ServicesApiRecentResource & { displayName?: string };

interface RecentCardProps {
  resource: RecentResourceWithDisplayName;
  workspaceId: number;
  index: number;
}

const DS_NODE_TYPE_LABELS: Record<string, string> = {
  EMAIL: 'Email',
  COMPONENT: 'Component',
  WHATSAPP: 'WhatsApp',
  FOLDER: 'Folder',
};

const DS_NODE_TYPE_ICONS: Record<string, IconName> = {
  EMAIL: 'email',
  COMPONENT: 'blocks',
  WHATSAPP: 'chat',
  FOLDER: 'folder',
};

const CAMPAIGN_TYPE_LABELS: Record<string, string> = {
  triggered_broadcast: 'API-Triggered Broadcast',
  behavioral: 'Campaign',
  seg_attr: 'Campaign',
  date: 'Campaign',
  form: 'Campaign',
  webhook: 'Campaign',
  transactional: 'Campaign',
  relationship: 'Campaign',
  object: 'Campaign',
};

function TruncatedTitle({ children }: { children: React.ReactNode }) {
  const [isTruncated, setIsTruncated] = useState(false);
  const [textContent, setTextContent] = useState<string | undefined>(undefined);

  const checkTruncation = useCallback((node: HTMLDivElement | null) => {
    if (node != null) {
      const truncated = node.scrollHeight > node.clientHeight;
      const content = node.textContent ?? undefined;
      setIsTruncated((previous) => (previous === truncated ? previous : truncated));
      setTextContent((previous) => (previous === content ? previous : content));
    }
  }, []);

  return (
    <Tooltip content={textContent} isCustom placement="bottom" isDisabled={!isTruncated}>
      {(context) => (
        <div
          className={styles.cardTitle}
          ref={(node) => {
            checkTruncation(node);
            context.reference.setReference(node);
          }}
          {...context.reference.getReferenceProps()}
        >
          {children}
        </div>
      )}
    </Tooltip>
  );
}

function RecentCard({ resource, workspaceId, index }: RecentCardProps) {
  const { track } = useAnalytics();
  const {
    name,
    campaignType,
    dsNodeType,
    isLoading: isResourceLoading,
    isNotFound,
  } = useResourceInfo(workspaceId, resource.type, resource.id);

  if (isNotFound && resource.displayName == null) {
    return null;
  }

  let typeLabel: string;
  let typeIcon: IconName;
  switch (resource.type) {
    case 'campaign':
      typeLabel = campaignType != null ? CAMPAIGN_TYPE_LABELS[campaignType] ?? 'Campaign' : 'Campaign';
      typeIcon = 'campaigns';
      break;
    case 'newsletter':
      typeLabel = 'Broadcast';
      typeIcon = 'broadcast';
      break;
    case 'transactional':
      typeLabel = 'Transactional';
      typeIcon = 'transactional-api';
      break;
    case 'segment':
      typeLabel = 'Segment';
      typeIcon = 'segment';
      break;
    case 'in_app_broadcast':
      typeLabel = 'Anonymous';
      typeIcon = 'anonymous';
      break;
    case 'ds_node':
      typeLabel = dsNodeType != null ? DS_NODE_TYPE_LABELS[dsNodeType] ?? 'Design Studio' : 'Design Studio';
      typeIcon = dsNodeType != null ? DS_NODE_TYPE_ICONS[dsNodeType] ?? 'design-studio' : 'design-studio';
      break;
    case 'snippet':
      typeLabel = 'Snippet';
      typeIcon = 'editor-code';
      break;
    default: {
      const _exhaustive: never = resource.type;
      typeLabel = String(_exhaustive);
      typeIcon = 'campaigns';
    }
  }

  // DS nodes need the type resolved before we can build the correct href
  if (resource.type === 'ds_node' && dsNodeType == null && isResourceLoading) {
    return <RecentCardSkeleton />;
  }

  let href = resourceHref(workspaceId, resource);
  if (resource.type === 'ds_node' && dsNodeType === 'FOLDER') {
    href = generatePath('/workspaces/:workspaceId/design-studio/dashboard', {
      workspaceId: String(workspaceId),
    });
  }

  return (
    <PlainLink
      href={href}
      className={styles.cardLink}
      onClick={() =>
        track('home', 'recentCardClicked', {
          'Resource Type': resource.type,
          Position: index + 1,
          'Is Create Action': false,
        })
      }
    >
      <div className={styles.card}>
        <Flex alignItems="center" justifyContent="space-between">
          <Flex alignItems="center" gap="100">
            <LabelIcon name={typeIcon} color="teal" shape="square" size="sm" />
            <Text size="xs" color="subtle">
              {typeLabel}
            </Text>
          </Flex>
          <Text size="xs" color="subtle">
            <FormattedRelativeTime date={resource.updated_at * 1000} />
          </Text>
        </Flex>
        <TruncatedTitle>
          {resource.displayName ?? name ?? <SkeletonShape style={{ width: '80%', height: 16 }} />}
        </TruncatedTitle>
      </div>
    </PlainLink>
  );
}

function RecentCardSkeleton() {
  return (
    <div className={styles.cardLink}>
      <div className={styles.card}>
        <Flex alignItems="center" justifyContent="space-between">
          <Flex alignItems="center" gap="100">
            <SkeletonShape style={{ width: 24, height: 24, borderRadius: 4 }} />
            <SkeletonShape style={{ width: 60, height: 14 }} />
          </Flex>
          <SkeletonShape style={{ width: 50, height: 14 }} />
        </Flex>
        <SkeletonShape style={{ width: '80%', height: 18 }} />
      </div>
    </div>
  );
}

interface CreateCardItem {
  label: string;
  description: string;
  icon: IconName;
  getHref: (workspaceId: string) => string;
}

const CREATE_CARDS: CreateCardItem[] = [
  {
    label: 'Start a campaign',
    description: 'Automate messages based on what people do',
    icon: 'campaigns',
    getHref: (wid) => generatePath('/workspaces/:workspaceId/journeys/campaigns/new', { workspaceId: wid }),
  },
  {
    label: 'Send a broadcast',
    description: 'Reach your audience with a one-time message',
    icon: 'broadcast',
    getHref: (wid) => generatePath('/workspaces/:workspaceId/journeys/broadcasts/new', { workspaceId: wid }),
  },
  {
    label: 'Build a segment',
    description: 'Group people by behavior or attributes',
    icon: 'segment',
    getHref: (wid) => generatePath('/workspaces/:workspaceId/journeys/segments/explore', { workspaceId: wid }),
  },
  {
    label: 'Set a goal',
    description: 'Track conversions that matter to your business',
    icon: 'goal',
    getHref: (wid) => generatePath('/workspaces/:workspaceId/journeys/goals/new', { workspaceId: wid }),
  },
];

function CreateCard({
  item,
  workspaceId,
  index,
  totalRecents,
}: {
  item: CreateCardItem;
  workspaceId: number;
  index: number;
  totalRecents: number;
}) {
  const { track } = useAnalytics();
  const href = item.getHref(String(workspaceId));

  return (
    <PlainLink
      href={href}
      className={styles.cardLink}
      onClick={() =>
        track('home', 'recentCardClicked', {
          'Resource Type': 'create',
          Position: totalRecents + index + 1,
          'Is Create Action': true,
        })
      }
    >
      <div className={styles.createCard}>
        <svg className={styles.createCardBorder} aria-hidden>
          <rect
            x="0.5"
            y="0.5"
            rx="4"
            ry="4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
            strokeDasharray="10 6"
            style={{ width: 'calc(100% - 1px)', height: 'calc(100% - 1px)' }}
          />
        </svg>
        <LabelIcon name={item.icon} color="grey" shape="square" size="sm" />
        <Flex direction="column" gap="025">
          <Text size="s" weight="semibold">
            {item.label}
          </Text>
          <Text size="xs" color="subtle">
            {item.description}
          </Text>
        </Flex>
      </div>
    </PlainLink>
  );
}

const THIRTY_DAYS_MS = 30 * 86400 * 1000;

export function areRecentsStale(recents: ServicesApiRecentResource[] | undefined): boolean {
  if (recents == null || recents.length === 0) return false;
  const cutoff = Date.now() - THIRTY_DAYS_MS;
  return recents.every((r) => r.updated_at * 1000 < cutoff);
}

interface RecentsRowProps {
  recents: RecentResourceWithDisplayName[] | undefined;
  isLoading: boolean;
  workspaceId: number;
}

export function RecentsRow({ recents, isLoading, workspaceId }: RecentsRowProps) {
  if (isLoading) {
    return (
      <Scrollable overflow="x" display="flex" gap="200" className={styles.container}>
        {Array.from({ length: 5 }, (_, index) => (
          <RecentCardSkeleton key={index} />
        ))}
      </Scrollable>
    );
  }

  return (
    <Scrollable overflow="x" display="flex" gap="200" className={styles.container} withEdgeIndicators>
      {recents?.map((resource, index) => (
        <RecentCard
          key={`${resource.type}-${resource.id}`}
          resource={resource}
          workspaceId={workspaceId}
          index={index}
        />
      ))}

      {CREATE_CARDS.map((item, index) => (
        <CreateCard
          key={item.label}
          item={item}
          workspaceId={workspaceId}
          index={index}
          totalRecents={recents?.length ?? 0}
        />
      ))}
    </Scrollable>
  );
}
